import { Code, ConnectError } from '@connectrpc/connect';
import { DeliverableImportTypes } from '@mono/proto-clients';
const { DeliverableImportAction, DeliverableImportDecision, DeliverableImportVerdict, DeliverableCommitStatus, DeliverableFields, SourceRef, DeliverableImportProposal, WorkstreamSummary, DeliverableImportTotals, DeliverableCommitResult, GetDeliverableImportReviewResponse, StageDeliverableImportResponse, ResolveDeliverableImportProposalResponse, ReviseDeliverableImportProposalResponse, CommitDeliverableImportWorkstreamResponse } = DeliverableImportTypes;
/**
 * Deliverable number of the one fixture proposal the fake commit always
 * fails, so the dev demo has a real FAILED row to show the loud-failure
 * treatment against — a task write conflict is a realistic reason a single
 * proposal in an otherwise-successful commit batch fails.
 */ const FAILING_COMMIT_DELIVERABLE_NUMBER = 'C004';
const WORKSTREAMS = [
    {
        key: 'program-level',
        label: 'Program-Level (CLIN 0001)',
        workbookRowCount: 9
    },
    {
        key: 'clin-0002',
        label: 'CLIN 0002 — Help Desk Support',
        workbookRowCount: 12
    },
    {
        key: 'clin-0003',
        label: 'CLIN 0003 — Training Services',
        workbookRowCount: 8
    }
];
function monthlyOccurrences(startIso, count) {
    const dates = [];
    const [y, m, d] = startIso.split('-').map(Number);
    for(let i = 0; i < count; i++){
        const date = new Date(Date.UTC(y, m - 1 + i, d));
        dates.push(date.toISOString().slice(0, 10));
    }
    return dates;
}
/**
 * A realistic small workbook: 3 workstreams (including the literal
 * "program-level" one), a mix of CREATE / UPDATE / ALREADY_MATCHES /
 * NEEDS_CLARIFICATION proposals, one recurring proposal with 12 occurrence
 * dates, and a few proposals already decided — so every dev-route card kind
 * has something real to show.
 */ export function createSampleDeliverableImportFixture() {
    const programId = 'prog-fixture-001';
    const changeSetId = 'change-set-fixture-001';
    const source = (row)=>new SourceRef({
            fileName: 'FY26-Deliverables-Schedule.xlsx',
            sourceArtifactId: 'artifact-fixture-workbook',
            sheetName: 'Deliverables',
            rows: [
                row
            ]
        });
    const proposals = [
        new DeliverableImportProposal({
            id: 'prop-001',
            changeSetId,
            workstreamKey: 'program-level',
            workstreamLabel: 'Program-Level (CLIN 0001)',
            ordinal: 1,
            action: DeliverableImportAction.CREATE,
            decision: DeliverableImportDecision.PENDING,
            proposed: new DeliverableFields({
                deliverableNumber: 'A001',
                title: 'Monthly Status Report',
                periodLabel: 'Monthly',
                periodOfPerformanceId: 'pop-base',
                pwsSections: [
                    '3.1'
                ],
                responsiblePerson: 'Program Manager',
                clinNumber: '0001',
                frequency: 'monthly',
                firstDueDate: '2026-01-05',
                seriesEndDate: '2026-12-05',
                occurrenceDates: monthlyOccurrences('2026-01-05', 12),
                dueDateNotes: 'Due the 5th business day of each month.'
            }),
            occurrenceCount: 12,
            source: source(4)
        }),
        new DeliverableImportProposal({
            id: 'prop-002',
            changeSetId,
            workstreamKey: 'program-level',
            workstreamLabel: 'Program-Level (CLIN 0001)',
            ordinal: 2,
            action: DeliverableImportAction.ALREADY_MATCHES,
            decision: DeliverableImportDecision.APPROVED,
            existingTaskId: 'task-pmp-001',
            proposed: new DeliverableFields({
                deliverableNumber: 'A002',
                title: 'Program Management Plan',
                periodLabel: 'One-time',
                clinNumber: '0001',
                responsiblePerson: 'Program Manager',
                frequency: 'one-time',
                firstDueDate: '2026-01-30'
            }),
            existing: new DeliverableFields({
                deliverableNumber: 'A002',
                title: 'Program Management Plan',
                periodLabel: 'One-time',
                clinNumber: '0001',
                responsiblePerson: 'Program Manager',
                frequency: 'one-time',
                firstDueDate: '2026-01-30'
            }),
            occurrenceCount: 1,
            source: source(5),
            decidedBy: 'jordan.lee@example.com',
            decidedAt: '2026-09-20T14:03:00Z'
        }),
        new DeliverableImportProposal({
            id: 'prop-003',
            changeSetId,
            workstreamKey: 'program-level',
            workstreamLabel: 'Program-Level (CLIN 0001)',
            ordinal: 3,
            action: DeliverableImportAction.NEEDS_CLARIFICATION,
            decision: DeliverableImportDecision.PENDING,
            proposed: new DeliverableFields({
                deliverableNumber: 'A003',
                title: 'Contractor Performance Assessment Input',
                periodLabel: 'Annual',
                clinNumber: '0001',
                frequency: 'annually'
            }),
            clarificationQuestion: 'The workbook lists two possible owners ("PM" and "Contracts Lead") for this row — which one is responsible?',
            occurrenceCount: 1,
            source: source(6)
        }),
        new DeliverableImportProposal({
            id: 'prop-004',
            changeSetId,
            workstreamKey: 'clin-0002',
            workstreamLabel: 'CLIN 0002 — Help Desk Support',
            ordinal: 1,
            action: DeliverableImportAction.CREATE,
            decision: DeliverableImportDecision.PENDING,
            proposed: new DeliverableFields({
                deliverableNumber: 'B001',
                title: 'Help Desk Ticket Log',
                periodLabel: 'Weekly',
                clinNumber: '0002',
                responsiblePerson: 'Help Desk Lead',
                frequency: 'weekly',
                firstDueDate: '2026-01-09'
            }),
            occurrenceCount: 52,
            source: source(11)
        }),
        new DeliverableImportProposal({
            id: 'prop-005',
            changeSetId,
            workstreamKey: 'clin-0002',
            workstreamLabel: 'CLIN 0002 — Help Desk Support',
            ordinal: 2,
            action: DeliverableImportAction.UPDATE,
            decision: DeliverableImportDecision.PENDING,
            existingTaskId: 'task-csat-001',
            proposed: new DeliverableFields({
                deliverableNumber: 'B002',
                title: 'Customer Satisfaction Survey Results',
                periodLabel: 'Quarterly',
                clinNumber: '0002',
                responsiblePerson: 'Help Desk Lead',
                frequency: 'quarterly',
                firstDueDate: '2026-01-15',
                dueDateNotes: 'Moved up one week per contracting officer request.'
            }),
            existing: new DeliverableFields({
                deliverableNumber: 'B002',
                title: 'Customer Satisfaction Survey Results',
                periodLabel: 'Quarterly',
                clinNumber: '0002',
                responsiblePerson: 'Support Manager',
                frequency: 'quarterly',
                firstDueDate: '2026-01-22'
            }),
            changedFields: [
                'responsiblePerson',
                'firstDueDate',
                'dueDateNotes'
            ],
            occurrenceCount: 4,
            source: source(12)
        }),
        new DeliverableImportProposal({
            id: 'prop-006',
            changeSetId,
            workstreamKey: 'clin-0002',
            workstreamLabel: 'CLIN 0002 — Help Desk Support',
            ordinal: 3,
            action: DeliverableImportAction.CREATE,
            decision: DeliverableImportDecision.REJECTED,
            proposed: new DeliverableFields({
                deliverableNumber: 'B003',
                title: 'Help Desk Ticket Log (duplicate)',
                periodLabel: 'Weekly',
                clinNumber: '0002',
                frequency: 'weekly',
                firstDueDate: '2026-01-09'
            }),
            decisionRationale: 'Duplicate of B001 — same deliverable listed twice in the workbook.',
            occurrenceCount: 52,
            source: source(13),
            decidedBy: 'jordan.lee@example.com',
            decidedAt: '2026-09-21T09:12:00Z'
        }),
        new DeliverableImportProposal({
            id: 'prop-007',
            changeSetId,
            workstreamKey: 'clin-0003',
            workstreamLabel: 'CLIN 0003 — Training Services',
            ordinal: 1,
            action: DeliverableImportAction.CREATE,
            decision: DeliverableImportDecision.EDITED_APPROVED,
            proposed: new DeliverableFields({
                deliverableNumber: 'C001',
                title: 'Training Completion Certificates',
                periodLabel: 'As needed',
                clinNumber: '0003',
                responsiblePerson: 'Training Coordinator',
                frequency: 'as-needed',
                dueDateNotes: 'Submitted within 5 business days of course completion.'
            }),
            revisionReason: 'Corrected responsible person from "TBD" to Training Coordinator.',
            occurrenceCount: 1,
            source: source(18),
            decidedBy: 'jordan.lee@example.com',
            decidedAt: '2026-09-22T16:40:00Z'
        }),
        new DeliverableImportProposal({
            id: 'prop-008',
            changeSetId,
            workstreamKey: 'clin-0003',
            workstreamLabel: 'CLIN 0003 — Training Services',
            ordinal: 2,
            action: DeliverableImportAction.ALREADY_MATCHES,
            decision: DeliverableImportDecision.PENDING,
            existingTaskId: 'task-training-plan-001',
            proposed: new DeliverableFields({
                deliverableNumber: 'C002',
                title: 'Annual Training Plan',
                periodLabel: 'Annual',
                clinNumber: '0003',
                responsiblePerson: 'Training Coordinator',
                frequency: 'annually',
                firstDueDate: '2026-02-01'
            }),
            existing: new DeliverableFields({
                deliverableNumber: 'C002',
                title: 'Annual Training Plan',
                periodLabel: 'Annual',
                clinNumber: '0003',
                responsiblePerson: 'Training Coordinator',
                frequency: 'annually',
                firstDueDate: '2026-02-01'
            }),
            occurrenceCount: 1,
            source: source(19)
        }),
        new DeliverableImportProposal({
            id: 'prop-009',
            changeSetId,
            workstreamKey: 'clin-0003',
            workstreamLabel: 'CLIN 0003 — Training Services',
            ordinal: 3,
            action: DeliverableImportAction.CREATE,
            decision: DeliverableImportDecision.SUPERSEDED,
            proposed: new DeliverableFields({
                deliverableNumber: 'C003',
                title: 'Vendor Training Roster',
                periodLabel: 'Quarterly',
                clinNumber: '0003',
                responsiblePerson: 'TBD',
                frequency: 'quarterly',
                firstDueDate: '2026-02-10',
                occurrenceDates: [
                    '2026-02-10',
                    '2026-05-10',
                    '2026-08-10',
                    '2026-11-10'
                ]
            }),
            occurrenceCount: 4,
            source: source(20)
        }),
        new DeliverableImportProposal({
            id: 'prop-010',
            changeSetId,
            workstreamKey: 'clin-0003',
            workstreamLabel: 'CLIN 0003 — Training Services',
            ordinal: 3,
            action: DeliverableImportAction.CREATE,
            decision: DeliverableImportDecision.PENDING,
            proposed: new DeliverableFields({
                deliverableNumber: 'C003',
                title: 'Vendor Training Roster',
                periodLabel: 'Quarterly',
                clinNumber: '0003',
                responsiblePerson: 'Training Coordinator',
                frequency: 'quarterly',
                firstDueDate: '2026-02-17',
                occurrenceDates: [
                    '2026-02-17',
                    '2026-05-17',
                    '2026-08-17',
                    '2026-11-17'
                ]
            }),
            revisionReason: 'Corrected responsible person and pushed the due date out one week.',
            supersedesProposalId: 'prop-009',
            occurrenceCount: 4,
            source: source(20)
        }),
        new DeliverableImportProposal({
            id: 'prop-011',
            changeSetId,
            workstreamKey: 'clin-0003',
            workstreamLabel: 'CLIN 0003 — Training Services',
            ordinal: 5,
            action: DeliverableImportAction.UPDATE,
            decision: DeliverableImportDecision.APPROVED,
            existingTaskId: 'task-vendor-cert-001',
            proposed: new DeliverableFields({
                deliverableNumber: FAILING_COMMIT_DELIVERABLE_NUMBER,
                title: 'Vendor Certification Tracker',
                periodLabel: 'Quarterly',
                clinNumber: '0003',
                responsiblePerson: 'Training Coordinator',
                frequency: 'quarterly',
                firstDueDate: '2026-02-20'
            }),
            existing: new DeliverableFields({
                deliverableNumber: FAILING_COMMIT_DELIVERABLE_NUMBER,
                title: 'Vendor Certification Tracker',
                periodLabel: 'Quarterly',
                clinNumber: '0003',
                responsiblePerson: 'Training Coordinator',
                frequency: 'quarterly',
                firstDueDate: '2026-02-13'
            }),
            changedFields: [
                'firstDueDate'
            ],
            occurrenceCount: 1,
            source: source(21),
            decidedBy: 'jordan.lee@example.com',
            decidedAt: '2026-09-23T10:00:00Z'
        })
    ];
    return {
        programId,
        changeSetId,
        fileName: 'FY26-Deliverables-Schedule.xlsx',
        sheetName: 'Deliverables',
        proposals
    };
}
/**
 * A fully-resolved import: every proposal is either committed-and-verified,
 * already-matched-and-committed, or rejected — nothing pending, nothing
 * uncommitted, nothing failed or unverified. Used to demo and test
 * IMPORT_COMPLETE's unqualified success wording, which only a genuinely
 * clean import earns. Distinct program/change-set ids from the main sample
 * fixture so the two can be rendered side by side without colliding in the
 * review query cache.
 */ export function createCleanDeliverableImportFixture() {
    const programId = 'prog-fixture-clean-001';
    const changeSetId = 'change-set-fixture-clean-001';
    const source = (row)=>new SourceRef({
            fileName: 'FY26-Deliverables-Schedule-Clean.xlsx',
            sourceArtifactId: 'artifact-fixture-clean-workbook',
            sheetName: 'Deliverables',
            rows: [
                row
            ]
        });
    const proposals = [
        new DeliverableImportProposal({
            id: 'clean-prop-001',
            changeSetId,
            workstreamKey: 'clin-0002',
            workstreamLabel: 'CLIN 0002 — Help Desk Support',
            ordinal: 1,
            action: DeliverableImportAction.CREATE,
            decision: DeliverableImportDecision.COMMITTED,
            proposed: new DeliverableFields({
                deliverableNumber: 'D001',
                title: 'Weekly Status Call Notes',
                periodLabel: 'Weekly',
                clinNumber: '0002',
                responsiblePerson: 'Help Desk Lead',
                frequency: 'weekly',
                firstDueDate: '2026-01-09'
            }),
            occurrenceCount: 1,
            source: source(4),
            commitResult: new DeliverableCommitResult({
                proposalId: 'clean-prop-001',
                action: DeliverableImportAction.CREATE,
                taskId: 'task-clean-001',
                status: DeliverableCommitStatus.CREATED,
                verified: true,
                saved: new DeliverableFields({
                    firstDueDate: '2026-01-09'
                }),
                savedOccurrenceDates: [
                    '2026-01-09'
                ]
            })
        }),
        new DeliverableImportProposal({
            id: 'clean-prop-002',
            changeSetId,
            workstreamKey: 'clin-0002',
            workstreamLabel: 'CLIN 0002 — Help Desk Support',
            ordinal: 2,
            action: DeliverableImportAction.UPDATE,
            decision: DeliverableImportDecision.COMMITTED,
            existingTaskId: 'task-csat-clean',
            proposed: new DeliverableFields({
                deliverableNumber: 'D002',
                title: 'Customer Satisfaction Survey Results',
                periodLabel: 'Quarterly',
                clinNumber: '0002',
                responsiblePerson: 'Help Desk Lead',
                frequency: 'quarterly',
                firstDueDate: '2026-01-15'
            }),
            changedFields: [
                'firstDueDate'
            ],
            occurrenceCount: 1,
            source: source(5),
            commitResult: new DeliverableCommitResult({
                proposalId: 'clean-prop-002',
                action: DeliverableImportAction.UPDATE,
                taskId: 'task-csat-clean',
                status: DeliverableCommitStatus.UPDATED,
                verified: true,
                saved: new DeliverableFields({
                    firstDueDate: '2026-01-15'
                }),
                savedOccurrenceDates: [
                    '2026-01-15'
                ]
            })
        }),
        new DeliverableImportProposal({
            id: 'clean-prop-003',
            changeSetId,
            workstreamKey: 'clin-0003',
            workstreamLabel: 'CLIN 0003 — Training Services',
            ordinal: 1,
            action: DeliverableImportAction.ALREADY_MATCHES,
            decision: DeliverableImportDecision.COMMITTED,
            existingTaskId: 'task-training-plan-clean',
            proposed: new DeliverableFields({
                deliverableNumber: 'D003',
                title: 'Annual Training Plan',
                periodLabel: 'Annual',
                clinNumber: '0003',
                responsiblePerson: 'Training Coordinator',
                frequency: 'annually',
                firstDueDate: '2026-02-01'
            }),
            existing: new DeliverableFields({
                deliverableNumber: 'D003',
                title: 'Annual Training Plan',
                periodLabel: 'Annual',
                clinNumber: '0003',
                responsiblePerson: 'Training Coordinator',
                frequency: 'annually',
                firstDueDate: '2026-02-01'
            }),
            occurrenceCount: 1,
            source: source(6),
            commitResult: new DeliverableCommitResult({
                proposalId: 'clean-prop-003',
                action: DeliverableImportAction.ALREADY_MATCHES,
                taskId: 'task-training-plan-clean',
                status: DeliverableCommitStatus.UPDATED,
                verified: true,
                saved: new DeliverableFields({
                    firstDueDate: '2026-02-01'
                }),
                savedOccurrenceDates: []
            })
        }),
        new DeliverableImportProposal({
            id: 'clean-prop-004',
            changeSetId,
            workstreamKey: 'clin-0003',
            workstreamLabel: 'CLIN 0003 — Training Services',
            ordinal: 2,
            action: DeliverableImportAction.CREATE,
            decision: DeliverableImportDecision.REJECTED,
            proposed: new DeliverableFields({
                deliverableNumber: 'D004',
                title: 'Duplicate Vendor Roster',
                periodLabel: 'Quarterly',
                clinNumber: '0003',
                frequency: 'quarterly',
                firstDueDate: '2026-02-10'
            }),
            decisionRationale: 'Duplicate row — already covered by D003.',
            occurrenceCount: 1,
            source: source(7),
            decidedBy: 'jordan.lee@example.com',
            decidedAt: '2026-09-24T09:00:00Z'
        })
    ];
    return {
        programId,
        changeSetId,
        fileName: 'FY26-Deliverables-Schedule-Clean.xlsx',
        sheetName: 'Deliverables',
        proposals
    };
}
const LARGE_FIXTURE_WORKSTREAM_COUNT = 20;
const LARGE_FIXTURE_PROPOSALS_PER_WORKSTREAM = 100;
const LARGE_FIXTURE_ACTIONS = [
    DeliverableImportAction.CREATE,
    DeliverableImportAction.UPDATE,
    DeliverableImportAction.ALREADY_MATCHES,
    DeliverableImportAction.NEEDS_CLARIFICATION
];
const LARGE_FIXTURE_DECISIONS = [
    DeliverableImportDecision.PENDING,
    DeliverableImportDecision.APPROVED,
    DeliverableImportDecision.EDITED_APPROVED,
    DeliverableImportDecision.REJECTED
];
/**
 * A ~20-workstream, ~2000-proposal import — the scale a real large workbook
 * produces — generated in a loop rather than hand-written. Actions and
 * decisions cycle through every combination so each card kind has plenty of
 * rows to render; the very first proposal is a recurring CREATE with a
 * 130-date occurrence list, the longest shape the occurrence-list UI has to
 * collapse. Used by the dev demo page to prove the card family holds up at
 * real scale (see DevDeliverableImportDemo.tsx).
 */ export function createLargeDeliverableImportFixture() {
    const programId = 'prog-fixture-large-001';
    const changeSetId = 'change-set-fixture-large-001';
    const fileName = 'FY26-Deliverables-Schedule-Large.xlsx';
    const sheetName = 'Deliverables';
    const workstreams = Array.from({
        length: LARGE_FIXTURE_WORKSTREAM_COUNT
    }, (_, i)=>{
        const n = i + 1;
        return {
            key: `clin-large-${String(n).padStart(2, '0')}`,
            label: `CLIN ${1000 + n} — Bulk Workstream ${n}`,
            workbookRowCount: LARGE_FIXTURE_PROPOSALS_PER_WORKSTREAM + 4
        };
    });
    const source = (row)=>new SourceRef({
            fileName,
            sourceArtifactId: 'artifact-fixture-large-workbook',
            sheetName,
            rows: [
                row
            ]
        });
    const proposals = [];
    let row = 5;
    workstreams.forEach((ws, wi)=>{
        for(let i = 0; i < LARGE_FIXTURE_PROPOSALS_PER_WORKSTREAM; i++){
            const ordinal = i + 1;
            const action = LARGE_FIXTURE_ACTIONS[i % LARGE_FIXTURE_ACTIONS.length];
            const decision = action === DeliverableImportAction.NEEDS_CLARIFICATION ? DeliverableImportDecision.PENDING : LARGE_FIXTURE_DECISIONS[i % LARGE_FIXTURE_DECISIONS.length];
            const isRecurringAnchor = wi === 0 && i === 0;
            const occurrenceDates = isRecurringAnchor ? monthlyOccurrences('2026-01-01', 130) : i % 5 === 0 ? monthlyOccurrences('2026-01-01', 4) : [];
            const deliverableNumber = `L${String(wi + 1).padStart(2, '0')}-${String(ordinal).padStart(3, '0')}`;
            const hasExisting = action === DeliverableImportAction.ALREADY_MATCHES || action === DeliverableImportAction.UPDATE;
            const proposed = new DeliverableFields({
                deliverableNumber,
                title: `Bulk Deliverable ${wi + 1}-${ordinal}`,
                periodLabel: occurrenceDates.length > 1 ? 'Recurring' : 'One-time',
                clinNumber: String(1000 + wi + 1),
                responsiblePerson: 'Bulk Owner',
                frequency: occurrenceDates.length > 1 ? 'monthly' : 'one-time',
                firstDueDate: occurrenceDates[0] ?? '2026-01-15',
                occurrenceDates
            });
            proposals.push(new DeliverableImportProposal({
                id: `prop-large-${wi + 1}-${ordinal}`,
                changeSetId,
                workstreamKey: ws.key,
                workstreamLabel: ws.label,
                ordinal,
                action,
                decision,
                proposed,
                occurrenceCount: occurrenceDates.length || 1,
                source: source(row),
                ...hasExisting && {
                    existingTaskId: `task-large-${wi + 1}-${ordinal}`,
                    existing: action === DeliverableImportAction.UPDATE ? new DeliverableFields({
                        ...proposed,
                        responsiblePerson: 'Previous Owner'
                    }) : proposed,
                    ...action === DeliverableImportAction.UPDATE && {
                        changedFields: [
                            'responsiblePerson'
                        ]
                    }
                },
                ...action === DeliverableImportAction.NEEDS_CLARIFICATION && {
                    clarificationQuestion: 'The workbook lists two possible owners — which one is responsible?'
                },
                ...decision !== DeliverableImportDecision.PENDING && {
                    decidedBy: 'bulk-reviewer@example.com',
                    decidedAt: '2026-09-25T00:00:00Z'
                },
                ...decision === DeliverableImportDecision.REJECTED && {
                    decisionRationale: 'Bulk-rejected duplicate row.'
                }
            }));
            row += 1;
        }
    });
    return {
        programId,
        changeSetId,
        fileName,
        sheetName,
        proposals,
        workstreams
    };
}
function summarizeWorkstream(fixture, key, label, workbookRowCount) {
    const relevant = fixture.proposals.filter((p)=>p.workstreamKey === key && p.decision !== DeliverableImportDecision.SUPERSEDED);
    const count = (pred)=>relevant.filter(pred).length;
    const approved = count((p)=>p.decision === DeliverableImportDecision.APPROVED);
    const editedApproved = count((p)=>p.decision === DeliverableImportDecision.EDITED_APPROVED);
    const rejected = count((p)=>p.decision === DeliverableImportDecision.REJECTED);
    const pending = count((p)=>p.decision === DeliverableImportDecision.PENDING);
    const committed = count((p)=>p.decision === DeliverableImportDecision.COMMITTED);
    const commitFailed = count((p)=>p.decision === DeliverableImportDecision.COMMIT_FAILED);
    return new WorkstreamSummary({
        key,
        label,
        workbookRowCount,
        proposedCreates: count((p)=>p.action === DeliverableImportAction.CREATE),
        proposedUpdates: count((p)=>p.action === DeliverableImportAction.UPDATE),
        alreadyMatches: count((p)=>p.action === DeliverableImportAction.ALREADY_MATCHES),
        needsClarification: count((p)=>p.action === DeliverableImportAction.NEEDS_CLARIFICATION),
        approved,
        editedApproved,
        rejected,
        pending,
        committed,
        commitFailed,
        reviewed: approved + editedApproved + rejected,
        totalToReview: relevant.length
    });
}
function computeTotals(fixture) {
    const active = fixture.proposals.filter((p)=>p.decision !== DeliverableImportDecision.SUPERSEDED);
    const recurring = active.filter((p)=>(p.proposed?.occurrenceDates.length ?? 0) > 1);
    const asNeeded = active.filter((p)=>p.proposed?.frequency === 'as-needed');
    const oneTime = active.filter((p)=>!recurring.includes(p) && !asNeeded.includes(p));
    const scheduledOccurrences = active.reduce((sum, p)=>sum + (p.proposed?.occurrenceDates.length || 0), 0);
    return new DeliverableImportTotals({
        workbookRows: active.length + 4,
        headerRows: 4,
        deliverableRecords: active.length,
        scheduledOccurrences,
        unresolvedRows: active.filter((p)=>p.action === DeliverableImportAction.NEEDS_CLARIFICATION).length,
        recurringRecords: recurring.length,
        oneTimeRecords: oneTime.length,
        asNeededRecords: asNeeded.length
    });
}
function buildReview(fixture, workstreamKey, includeSuperseded) {
    const workstreams = (fixture.workstreams ?? WORKSTREAMS).map((w)=>summarizeWorkstream(fixture, w.key, w.label, w.workbookRowCount));
    const proposals = fixture.proposals.filter((p)=>{
        if (workstreamKey && p.workstreamKey !== workstreamKey) return false;
        if (!includeSuperseded && p.decision === DeliverableImportDecision.SUPERSEDED) return false;
        return true;
    });
    return new GetDeliverableImportReviewResponse({
        changeSetId: fixture.changeSetId,
        fileName: fixture.fileName,
        sheetName: fixture.sheetName,
        totals: computeTotals(fixture),
        workstreams,
        proposals
    });
}
let nextId = 1000;
function freshId(prefix) {
    nextId += 1;
    return `${prefix}-${nextId}`;
}
/**
 * Implements the five DeliverableImportService RPCs against an in-memory
 * fixture — the only way to exercise this feature until ryugu ships the real
 * handler. Resolve/Revise mutate the fixture in place; Commit validates
 * `expectedProposalIds` against the currently-approved set exactly as the
 * proto doc comment specifies, throwing FailedPrecondition on mismatch.
 */ export function makeFakeDeliverableImportClient(fixture) {
    return {
        async stageDeliverableImport (request) {
            const changeSetId = request.changeSetId || fixture.changeSetId;
            const proposalIds = [];
            for (const input of request.proposals ?? []){
                const id = freshId('prop');
                proposalIds.push(id);
                fixture.proposals.push(new DeliverableImportProposal({
                    id,
                    changeSetId,
                    workstreamKey: input.workstreamKey,
                    workstreamLabel: input.workstreamLabel,
                    ordinal: input.ordinal,
                    action: input.action,
                    decision: DeliverableImportDecision.PENDING,
                    existingTaskId: input.existingTaskId,
                    proposed: input.proposed,
                    source: input.source,
                    clarificationQuestion: input.clarificationQuestion,
                    occurrenceCount: input.proposed?.occurrenceDates?.length || (input.proposed ? 1 : 0)
                }));
            }
            return new StageDeliverableImportResponse({
                changeSetId,
                proposalIds,
                review: buildReview(fixture, '', false)
            });
        },
        async getDeliverableImportReview (request) {
            return buildReview(fixture, request.workstreamKey ?? '', request.includeSuperseded ?? false);
        },
        async resolveDeliverableImportProposal (request) {
            const proposal = fixture.proposals.find((p)=>p.id === request.proposalId);
            if (!proposal) {
                throw new ConnectError(`no such proposal: ${request.proposalId}`, Code.NotFound);
            }
            if (request.verdict === DeliverableImportVerdict.APPROVE) {
                proposal.decision = DeliverableImportDecision.APPROVED;
            } else if (request.verdict === DeliverableImportVerdict.REJECT) {
                proposal.decision = DeliverableImportDecision.REJECTED;
            }
            // DEFER leaves the decision at PENDING — there is no DEFERRED decision
            // state, only a recorded rationale for revisiting it later.
            proposal.decisionRationale = request.rationale ?? '';
            proposal.decidedBy = 'dev-fixture-user@example.com';
            proposal.decidedAt = new Date().toISOString();
            const workstream = summarizeWorkstream(fixture, proposal.workstreamKey, proposal.workstreamLabel, (fixture.workstreams ?? WORKSTREAMS).find((w)=>w.key === proposal.workstreamKey)?.workbookRowCount ?? 0);
            return new ResolveDeliverableImportProposalResponse({
                proposal,
                workstream
            });
        },
        async reviseDeliverableImportProposal (request) {
            const original = fixture.proposals.find((p)=>p.id === request.proposalId);
            if (!original) {
                throw new ConnectError(`no such proposal: ${request.proposalId}`, Code.NotFound);
            }
            original.decision = DeliverableImportDecision.SUPERSEDED;
            const revised = new DeliverableImportProposal({
                id: freshId('prop'),
                changeSetId: original.changeSetId,
                workstreamKey: original.workstreamKey,
                workstreamLabel: original.workstreamLabel,
                ordinal: original.ordinal,
                action: request.action ? request.action : original.action,
                decision: DeliverableImportDecision.PENDING,
                existingTaskId: original.existingTaskId,
                proposed: request.revised,
                existing: original.existing,
                source: original.source,
                revisionReason: request.reason ?? '',
                supersedesProposalId: original.id,
                occurrenceCount: request.revised?.occurrenceDates?.length || 1
            });
            fixture.proposals.push(revised);
            const workstream = summarizeWorkstream(fixture, revised.workstreamKey, revised.workstreamLabel, (fixture.workstreams ?? WORKSTREAMS).find((w)=>w.key === revised.workstreamKey)?.workbookRowCount ?? 0);
            return new ReviseDeliverableImportProposalResponse({
                proposal: revised,
                supersededProposalId: original.id,
                workstream
            });
        },
        async commitDeliverableImportWorkstream (request) {
            const approved = fixture.proposals.filter((p)=>p.workstreamKey === request.workstreamKey && (p.decision === DeliverableImportDecision.APPROVED || p.decision === DeliverableImportDecision.EDITED_APPROVED));
            const approvedIds = new Set(approved.map((p)=>p.id));
            const expectedIds = new Set(request.expectedProposalIds ?? []);
            const matches = approvedIds.size === expectedIds.size && [
                ...approvedIds
            ].every((id)=>expectedIds.has(id));
            if (!matches) {
                throw new ConnectError('the approved proposal set for this workstream changed since it was last loaded — reload and try again', Code.FailedPrecondition);
            }
            let created = 0;
            let updated = 0;
            let failed = 0;
            let verified = 0;
            const results = approved.map((proposal)=>{
                if (proposal.proposed?.deliverableNumber === FAILING_COMMIT_DELIVERABLE_NUMBER) {
                    failed += 1;
                    proposal.decision = DeliverableImportDecision.COMMIT_FAILED;
                    const result = new DeliverableCommitResult({
                        proposalId: proposal.id,
                        action: proposal.action,
                        status: DeliverableCommitStatus.FAILED,
                        verified: false,
                        error: `Task write failed: existing task ${proposal.existingTaskId || 'unknown'} was locked by another edit — try again.`
                    });
                    proposal.commitResult = result;
                    return result;
                }
                const status = proposal.action === DeliverableImportAction.CREATE ? DeliverableCommitStatus.CREATED : DeliverableCommitStatus.UPDATED;
                if (status === DeliverableCommitStatus.CREATED) created += 1;
                else updated += 1;
                verified += 1;
                proposal.decision = DeliverableImportDecision.COMMITTED;
                const result = new DeliverableCommitResult({
                    proposalId: proposal.id,
                    action: proposal.action,
                    taskId: proposal.existingTaskId || freshId('task'),
                    status,
                    verified: true,
                    saved: proposal.proposed,
                    savedOccurrenceDates: proposal.proposed?.occurrenceDates ?? []
                });
                proposal.commitResult = result;
                return result;
            });
            const workstreamKey = request.workstreamKey ?? '';
            const workstreamRoster = fixture.workstreams ?? WORKSTREAMS;
            const workstream = summarizeWorkstream(fixture, workstreamKey, workstreamRoster.find((w)=>w.key === workstreamKey)?.label ?? workstreamKey, workstreamRoster.find((w)=>w.key === workstreamKey)?.workbookRowCount ?? 0);
            return new CommitDeliverableImportWorkstreamResponse({
                results,
                workstream,
                created,
                updated,
                failed,
                verified
            });
        }
    };
}
